import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "../config/axios";
import PageWrapper from "../components/common/PageWrapper";
import Header from "../components/common/Header";
import BodyWrapper from "../components/common/BodyWrapper";
import Footer from "../components/common/Footer";
import ListingComponent from "../components/newsEvents/ListingComponent";
import ExpandedComponent from "../components/newsEvents/ExpandedComponent";
import { useGeneral } from "../components/contexts/GeneralContext";
import FullScreenLoading from "../components/common/loading/FullScreenLoading";
import ApiErrorMessage from "../components/common/ApiErrorMessage";
import NoItemsFound from "../components/common/NoItemsFound";
import GeneralButton from "../components/common/GeneralButton";
import ContainerLoading from "../components/common/loading/ContainerLoading";
import SearchBar from "../components/common/SearchBar";
import SelectBox from "../components/common/SelectBox";

/* ---------------------- Custom Hook: Page Content ---------------------- */
const usePageContent = (page, query, filter) => {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState({
    items: [],
    pagination: {},
    availableYears: [],
  });
  const [error, setError] = useState("");
  const { discountCoupons } = useGeneral();

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `/news-events?page=${page}&q=${query}${
            filter ? `&year=${filter}` : ""
          }`
        );

        if (active) {
          setContent((prev) => ({
            items: page === 1 ? data.items : [...prev.items, ...data.items],
            availableYears: data.availableYears,
            pagination: data.pagination,
          }));
        }
      } catch (err) {
        console.error(err);
        if (active)
          setError("Failed to load news & events. Please try again later.");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [page, query, filter]);

  return { content, loading, error, discountCoupons };
};

/* ---------------------- Custom Hook: Scroll to Top ---------------------- */
const useScrollToTop = () => {
  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timer);
  }, []);
};

/* ---------------------- Main Component ---------------------- */
const NewsEventsPage = () => {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState(""); // triggers API
  const [inputValue, setInputValue] = useState(""); // controlled input
  const [selectValue, setSelectValue] = useState(""); // filter value
  const { content, loading, error, discountCoupons } = usePageContent(
    page,
    query,
    selectValue
  );

  const [activeState, setActiveState] = useState({
    id: null,
    item: null,
    loading: false,
    error: "",
  });

  useScrollToTop();

  /* ---------- Set default active item on initial load ---------- */
  useEffect(() => {
    if (content?.items?.length > 0) {
      setActiveState((prev) => ({ ...prev, id: content.items[0]._id }));
    }
  }, [content.items]);

  /* ---------- Fetch active item details ---------- */
  useEffect(() => {
    if (!activeState.id) return;

    let active = true;
    setActiveState((prev) => ({ ...prev, loading: true, error: "" }));

    (async () => {
      try {
        const { data } = await axios.get(`/news-events/${activeState.id}`);
        if (active) setActiveState((prev) => ({ ...prev, item: data }));
      } catch (err) {
        console.error(err);
        if (active)
          setActiveState((prev) => ({
            ...prev,
            error: "Failed to load this news item.",
          }));
      } finally {
        if (active) setActiveState((prev) => ({ ...prev, loading: false }));
      }
    })();

    return () => {
      active = false;
    };
  }, [activeState.id]);

  /* ---------- Handlers ---------- */
  const handleItemClick = useCallback(
    (id) => {
      if (id === activeState.id) return;
      setActiveState((prev) => ({ ...prev, id }));
      setTimeout(() => {
        const lgBreakpoint = 1024;
        if (window.innerWidth >= lgBreakpoint) {
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          const expandedEl = document.getElementById("expanded-component");
          if (expandedEl)
            expandedEl.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    },
    [activeState.id]
  );

  const loadMore = useCallback(() => {
    if (page < content.pagination.pageCount) setPage((prev) => prev + 1);
  }, [page, content.pagination.pageCount]);

  const hasMore = useMemo(
    () => page < content.pagination.pageCount,
    [page, content.pagination.pageCount]
  );

  const handleSearch = (searchQuery) => {
    setPage(1);
    setQuery(searchQuery); // triggers API call
    setActiveState({ id: null, item: null, loading: false, error: "" });
  };

  const handleClearSearch = () => {
    setInputValue("");
    handleSearch("");
  };

  const handleSelectChange = (val) => {
    setSelectValue(val);
    setPage(1);
    setActiveState({ id: null, item: null, loading: false, error: "" });
  };
  function formatYearsArray(availableYears) {
    return availableYears.map((year) => ({
      label: year,
      value: year,
    }));
  }

  const formattedYears = formatYearsArray(content?.availableYears);

  /* ---------------------- Render ---------------------- */
  return (
    <>
      {loading && page === 1 && <FullScreenLoading />}
      <PageWrapper loading={loading}>
        <Header code={discountCoupons} />
        <BodyWrapper loading={loading} className="min-h-[400px]">
          <>
            {content?.pagination?.totalItems > 0 && (
              <div className="mb-4 flex flex-col  items-start md:items-center justify-between gap-2 md:mt-2 mt-0">
                <div className="flex-1 flex items-center justify-end gap-2 w-full">
                  <SearchBar
                    value={inputValue}
                    setValue={setInputValue}
                    onClear={handleClearSearch}
                    onSearch={() => handleSearch(inputValue)}
                  />
                  {content?.availableYears.length > 0 && (
                    <SelectBox
                      value={selectValue}
                      setValue={setSelectValue}
                      options={formattedYears}
                      placeholder="Choose a Year"
                      onSelect={handleSelectChange}
                    />
                  )}
                </div>
                {(query || selectValue) && (
                  <div className="flex items-center flex-wrap gap-2 mt-2 px-3 py-2 bg-[#D06F36]/10 rounded-lg w-full">
                    {query && (
                      <p className="text-sm text-gray-700">
                        Showing results for{" "}
                        <span className="text-[#D06F36]">"{query}"</span>
                      </p>
                    )}

                    {selectValue && (
                      <p className="text-sm text-gray-700 flex items-center gap-1">
                        Filter Applied:{" "}
                        <span className="text-[#D06F36]">
                          {
                            formattedYears.find((o) => o.value === selectValue)
                              ?.label
                          }
                        </span>
                        <button
                          onClick={() => handleSelectChange("")}
                          className="ml-1 text-xs text-[#D06F36] font-medium hover:underline transition-all duration-200"
                        >
                          ×
                        </button>
                      </p>
                    )}

                    {(query || selectValue) && (
                      <button
                        onClick={() => {
                          handleClearSearch();
                          handleSelectChange("");
                        }}
                        className="ml-auto text-xs text-[#D06F36] font-medium hover:underline transition-all duration-200"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {error ? (
              <ApiErrorMessage
                message1="Failed to load news & events"
                message2="Please check your connection and try again."
              />
            ) : !loading && content?.pagination?.totalItems <= 0 ? (
              <div className="flex items-center justify-center my-6 1xl:my-4">
                <img
                  src={require("../assets/images/comingSoon.png")}
                  alt="shadow"
                  className="w-full h-full xl:max-w-[40%]"
                />
              </div>
            ) : !loading &&
              content?.pagination?.totalItems > 0 &&
              content?.pagination?.totalFilteredItems === 0 ? (
              <NoItemsFound
                message1="No news or event found"
                message2="Please try a different search term."
              />
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-[1.5fr_600px] 2.5xl:grid-cols-[1.5fr_850px] gap-6 xl:gap-10 items-start">
                {/* LEFT COLUMN - LIST */}
                <div>
                  {content.items.map((item, index) => (
                    <ListingComponent
                      key={index}
                      item={item}
                      activeItem={activeState.id}
                      onClick={() => handleItemClick(item._id)}
                    />
                  ))}

                  {hasMore && (
                    <div className="flex justify-center mt-4 md:mt-6">
                      <GeneralButton
                        text="Load More"
                        title="Click to load more items"
                        onClick={loadMore}
                        bgColor="#D06F36"
                      />
                    </div>
                  )}

                  {loading && page >= 1 && (
                    <div className="flex justify-center mt-4">
                      <div className="w-5 h-5 border-4 border-tertiary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>

                {/* RIGHT COLUMN - EXPANDED VIEW */}
                <div id="expanded-component">
                  {activeState.loading ? (
                    <div className="h-[500px]">
                      <ContainerLoading />
                    </div>
                  ) : activeState.error ? (
                    <ApiErrorMessage
                      message1="Failed to load news details"
                      message2="Please try again later."
                    />
                  ) : activeState.item ? (
                    <ExpandedComponent item={activeState.item} />
                  ) : null}
                </div>
              </div>
            )}
          </>
        </BodyWrapper>
        <Footer />
      </PageWrapper>
    </>
  );
};

export default NewsEventsPage;
